/**
 * Chat Message Extension Registry
 *
 * Registry for plugin-provided chat message UI extensions.
 * Plugins register components that render inside chat messages
 * (personality badges, thought renderers, action buttons, etc.)
 * without core needing direct imports.
 *
 * Extension slots:
 *   - 'message-header'      — badges / indicators in the message header
 *   - 'message-footer'      — action buttons, quick replies below content
 *   - 'context-section'     — collapsible sections inside ContextViewer
 *   - 'assistant-message'   — full replacement for assistant-initiated messages
 *   - 'personality-badge'   — personality indicator in header
 *   - 'personality-panel'   — personality influence panel in context viewer
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import React from 'react';

// ============================================================
// Types
// ============================================================

export type ChatMessageSlot =
  | 'message-header'
  | 'message-footer'
  | 'context-section'
  | 'assistant-message'
  | 'personality-badge'
  | 'personality-panel';

export interface ChatMessageExtension {
  /** Unique extension ID */
  id: string;
  /** Plugin that registered this extension */
  pluginId: string;
  /** Slot where the extension renders */
  slot: ChatMessageSlot;
  /** React component (lazy or eager) */
  component: React.ComponentType<any>;
  /** Priority — higher renders first within the same slot */
  priority?: number;
  /**
   * Optional predicate — extension only renders when this returns true.
   * Receives the message metadata as input.
   */
  condition?: (messageMetadata: Record<string, any>) => boolean;
}

// ============================================================
// State
// ============================================================

interface ChatMessageExtensionState {
  extensions: ChatMessageExtension[];

  registerExtension: (ext: ChatMessageExtension) => void;
  unregisterExtension: (id: string) => void;
  unregisterPluginExtensions: (pluginId: string) => void;
  getExtensionsForSlot: (
    slot: ChatMessageSlot,
    metadata?: Record<string, any>,
  ) => ChatMessageExtension[];
  reset: () => void;
}

// ============================================================
// Store
// ============================================================

export const useChatMessageExtensionRegistry =
  create<ChatMessageExtensionState>()(
    subscribeWithSelector((set, get) => ({
      extensions: [],

      registerExtension(ext) {
        set((state) => {
          const filtered = state.extensions.filter((e) => e.id !== ext.id);
          return { extensions: [...filtered, ext] };
        });
      },

      unregisterExtension(id) {
        set((state) => ({
          extensions: state.extensions.filter((e) => e.id !== id),
        }));
      },

      unregisterPluginExtensions(pluginId) {
        set((state) => ({
          extensions: state.extensions.filter(
            (e) => e.pluginId !== pluginId,
          ),
        }));
      },

      getExtensionsForSlot(slot, metadata = {}) {
        return get()
          .extensions.filter(
            (e) =>
              e.slot === slot &&
              (!e.condition || e.condition(metadata)),
          )
          .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
      },

      reset() {
        set({ extensions: [] });
      },
    })),
  );
